<script setup lang="ts">
import { computed } from 'vue';

// The workspace seal: the brand chip's rounded-square silhouette carrying the
// tenant's monogram instead of the Q. One glyph system says both things at
// once - the chip is HQ (the platform, constant), the letter inside is the
// tenant (the scope, variable). The ACTIVE workspace is "minted" in the brand
// chip (yellow + navy, identical in light and dark, exactly like BrandQ);
// reachable-but-inactive workspaces render as un-minted ink seals with a
// deterministic treatment per slug (outline / filled / sunken), so tenants
// stay distinguishable at a glance without color entering the monochrome
// system. Decorative: callers keep the accessible name on their own context.
const props = withDefaults(
  defineProps<{
    name: string;
    slug?: string;
    minted?: boolean;
    size?: number;
  }>(),
  { slug: '', minted: false, size: 28 },
);

const monogram = computed(() => {
  const n = props.name.trim();
  return n ? n.charAt(0).toUpperCase() : '?';
});

// Stable ink treatment per workspace (hash of the slug, name as fallback),
// so the same tenant always wears the same seal across sessions and menus.
const TREATMENTS = ['outline', 'filled', 'sunken'] as const;
const treatment = computed(() => {
  const key = props.slug || props.name;
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return TREATMENTS[h % TREATMENTS.length];
});
</script>

<template>
  <span
    class="ws-seal"
    :class="minted ? 'ws-seal--minted' : `ws-seal--${treatment}`"
    :style="{ '--seal-size': `${size}px` }"
    aria-hidden="true"
  >{{ monogram }}</span>
</template>

<style scoped lang="scss">
.ws-seal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--seal-size);
  height: var(--seal-size);
  /* same corner proportion as the brand chip (14/64) */
  border-radius: calc(var(--seal-size) * 0.22);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: calc(var(--seal-size) * 0.5);
  line-height: 1;
  user-select: none;
}

/* Minted: the brand chip itself - yellow + navy, the same in both themes
   (matches BrandQ's baked-in colors; this is the one place yellow lives). */
.ws-seal--minted {
  background: #facc15;
  color: #0a1f44;
}

/* Un-minted ink treatments - monochrome, distinguishable without color.
   Context can re-aim the inks (e.g. the nav) via --seal-ink / --seal-bg. */
.ws-seal--outline {
  border: 1.5px solid var(--seal-ink, var(--ink-primary));
  color: var(--seal-ink, var(--ink-primary));
  background: transparent;
}
.ws-seal--filled {
  background: var(--seal-ink, var(--ink-primary));
  color: var(--seal-bg, var(--surface-panel));
}
.ws-seal--sunken {
  background: var(--surface-sunken);
  border: 1px solid var(--border-subtle);
  color: var(--seal-ink, var(--ink-primary));
}
</style>
